
import { useState, useEffect, useCallback } from 'react';

interface TimeSlot {
  start_time: string;
  end_time: string;
}

export default function Offer() {
  const [activeTab, setActiveTab] = useState<'review' | 'booking'>('review');
  
  // Review form state
  const [formData, setFormData] = useState({
    businessName: '',
    websiteUrl: '',
    email: '',
    workType: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // Booking state
  const [timeslots, setTimeslots] = useState<TimeSlot[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null);
  const [calendarMonth, setCalendarMonth] = useState<number>(new Date().getMonth());
  const [calendarYear, setCalendarYear] = useState<number>(new Date().getFullYear());
  const [bookingStep, setBookingStep] = useState(0);
  const [bookingForm, setBookingForm] = useState({
    customer_name: '',
    customer_phone: '',
    notes: ''
  });
  const [bookingStatus, setBookingStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const timeslotsApi = "https://readdy.ai/api/public/calendar/timeslots/20418aa2-ab35-4a62-a7ef-7317e5a1f3ff.220c8a41ece90fa0e0c5f84b69df28cdcc2b69891d1ac0e452218bbcf954fb1e";
  const appointmentsApi = "https://readdy.ai/api/public/calendar/appointments/20418aa2-ab35-4a62-a7ef-7317e5a1f3ff.220c8a41ece90fa0e0c5f84b69df28cdcc2b69891d1ac0e452218bbcf954fb1e";

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const fetchTimeslots = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(timeslotsApi);
      const data = await response.json();
      setTimeslots(data || []);
      return data || [];
    } catch (error) {
      console.error('Failed to fetch timeslots:', error);
      setTimeslots([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTimeslots().then((slots: TimeSlot[]) => {
      if (slots.length > 0) {
        const today = new Date().toISOString().split('T')[0];
        const availableDates = [...new Set(slots.map((slot: TimeSlot) => slot.start_time.split(' ')[0]))].sort();
        const targetDate = availableDates.find(date => date >= today) || availableDates[availableDates.length - 1];
        
        if (targetDate) {
          const [year, month] = targetDate.split('-').map(Number);
          setCalendarYear(year);
          setCalendarMonth(month - 1);
          setSelectedDate(targetDate);
        }
      }
    });
  }, [fetchTimeslots]);

  const getAvailableDates = useCallback(() => {
    return [...new Set(timeslots.map(slot => slot.start_time.split(' ')[0]))];
  }, [timeslots]);

  const getSlotsForDate = useCallback((date: string) => {
    return timeslots.filter(slot => slot.start_time.split(' ')[0] === date);
  }, [timeslots]);

  const formatTime = (timeString: string) => {
    const timePart = timeString.split(' ')[1];
    return timePart ? timePart.substring(0, 5) : '';
  };

  const formatDisplayDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });
  };

  const getDaysInMonth = (year: number, month: number) => new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (year: number, month: number) => new Date(year, month, 1).getDay();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams({
        businessName: formData.businessName,
        websiteUrl: formData.websiteUrl,
        email: formData.email,
        workType: formData.workType
      }).toString();

      const response = await fetch('https://readdy.ai/api/form/d61k3tccm4ll99gupjlg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ businessName: '', websiteUrl: '', email: '', workType: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleBookingSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedSlot) return;

    setBookingStatus('submitting');

    try {
      const response = await fetch(appointmentsApi, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_name: bookingForm.customer_name,
          customer_phone: bookingForm.customer_phone,
          start_time: selectedSlot.start_time,
          end_time: selectedSlot.end_time,
          notes: bookingForm.notes || ''
        })
      });

      if (response.ok) {
        setBookingStatus('success');
        await fetchTimeslots();
        setTimeout(() => {
          setBookingStep(0);
          setSelectedDate(null);
          setSelectedSlot(null);
          setBookingForm({ customer_name: '', customer_phone: '', notes: '' });
          setBookingStatus('idle');
        }, 3000);
      } else {
        setBookingStatus('error');
      }
    } catch {
      setBookingStatus('error');
    }
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(calendarYear, calendarMonth);
    const firstDay = getFirstDayOfMonth(calendarYear, calendarMonth);
    const availableDates = getAvailableDates();
    const days = [];

    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="h-9"></div>);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${calendarYear}-${String(calendarMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const isAvailable = availableDates.includes(dateStr);
      const isSelected = selectedDate === dateStr;

      days.push(
        <button
          key={day}
          type="button"
          onClick={() => isAvailable && setSelectedDate(dateStr)}
          disabled={!isAvailable}
          className={`h-9 w-9 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer
            ${isSelected 
              ? 'bg-[#FF6B35] text-white' 
              : isAvailable 
                ? 'text-[#1A1F36] hover:bg-[#FF6B35]/10' 
                : 'text-gray-300 cursor-not-allowed'
            }`}
        >
          {day}
        </button>
      );
    }

    return days;
  };

  const slotsForSelectedDate = selectedDate ? getSlotsForDate(selectedDate) : [];

  return (
    <section id="offer-section" className="bg-[#FAFBFC] py-28">
      <div className="max-w-[1000px] mx-auto px-6">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-[#FF6B35]/20 rounded-full text-sm mb-6 shadow-sm">
            <span className="w-1.5 h-1.5 bg-[#FF6B35] rounded-full animate-pulse"></span>
            <span className="text-[#1A1F36] font-medium">Get Started Today</span>
          </div>
          <h2 className="text-4xl lg:text-[42px] font-bold text-[#1A1F36] mb-6">
            Start With a <span className="text-[#FF6B35]">Free Digital Review</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-[700px] mx-auto">
            See your business the way your customers do—before they decide to call someone else.
          </p>
        </div>

        <div className="max-w-[720px] mx-auto">
          <div className="bg-white rounded-2xl overflow-hidden shadow-xl border border-gray-100">
            {/* Toggle Tabs */}
            <div className="flex bg-[#FAFBFC] p-1.5">
              <button
                onClick={() => setActiveTab('review')}
                className={`flex-1 py-3.5 px-6 text-sm font-semibold transition-all duration-300 cursor-pointer flex items-center justify-center gap-2 rounded-xl
                  ${activeTab === 'review' 
                    ? 'text-white bg-[#FF6B35] shadow-md' 
                    : 'text-gray-500 hover:text-[#1A1F36]'
                  }`}
              >
                <i className="ri-file-search-line text-lg w-5 h-5 flex items-center justify-center"></i>
                Get Free Review
              </button>
              <button
                onClick={() => setActiveTab('booking')}
                className={`flex-1 py-3.5 px-6 text-sm font-semibold transition-all duration-300 cursor-pointer flex items-center justify-center gap-2 rounded-xl
                  ${activeTab === 'booking' 
                    ? 'text-white bg-[#FF6B35] shadow-md' 
                    : 'text-gray-500 hover:text-[#1A1F36]'
                  }`}
              >
                <i className="ri-calendar-line text-lg w-5 h-5 flex items-center justify-center"></i>
                Book a Call
              </button>
            </div>

            {/* Content Area */}
            <div className="p-8 lg:p-10">
              {/* Free Review Tab */}
              {activeTab === 'review' && (
                <div>
                  {submitStatus === 'success' ? (
                    <div className="text-center py-8">
                      <div className="w-16 h-16 bg-[#FF6B35]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <i className="ri-check-line text-3xl text-[#FF6B35]"></i>
                      </div>
                      <h3 className="text-2xl font-bold text-[#1A1F36] mb-2">Request Received!</h3>
                      <p className="text-gray-600">We'll send your review within 48 hours.</p>
                      <button
                        onClick={() => setSubmitStatus('idle')}
                        className="mt-6 text-[#FF6B35] font-medium underline hover:no-underline cursor-pointer"
                      >
                        Submit another request
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="mb-6 p-4 bg-[#FF6B35]/5 rounded-xl border border-[#FF6B35]/10">
                        <h3 className="text-sm font-bold text-[#1A1F36] mb-3 flex items-center gap-2">
                          <i className="ri-gift-line text-[#FF6B35]"></i>
                          What You Get:
                        </h3>
                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {[
                            'Website performance assessment',
                            'Google Business Profile audit',
                            'Content and branding review',
                            'Clear action plan'
                          ].map((item, index) => (
                            <li key={index} className="flex items-center gap-2 text-sm text-gray-600">
                              <i className="ri-check-line text-[#FF6B35] flex-shrink-0"></i>
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <form id="review-form" onSubmit={handleSubmit} data-readdy-form className="space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label htmlFor="businessName" className="block text-sm font-medium text-[#1A1F36] mb-2">
                              Business Name *
                            </label>
                            <input
                              type="text"
                              id="businessName"
                              name="businessName"
                              value={formData.businessName}
                              onChange={handleChange}
                              required
                              className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FF6B35]/30 focus:border-[#FF6B35]"
                              placeholder="Your business name"
                            />
                          </div>
                          <div>
                            <label htmlFor="websiteUrl" className="block text-sm font-medium text-[#1A1F36] mb-2">
                              Website URL *
                            </label>
                            <input
                              type="url"
                              id="websiteUrl"
                              name="websiteUrl"
                              value={formData.websiteUrl}
                              onChange={handleChange}
                              required
                              className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FF6B35]/30 focus:border-[#FF6B35]"
                              placeholder="https://yourwebsite.com"
                            />
                          </div>
                        </div>

                        <div>
                          <label htmlFor="email" className="block text-sm font-medium text-[#1A1F36] mb-2">
                            Email *
                          </label>
                          <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FF6B35]/30 focus:border-[#FF6B35]"
                            placeholder="your@email.com"
                          />
                        </div>

                        <div>
                          <label htmlFor="workType" className="block text-sm font-medium text-[#1A1F36] mb-2">
                            What kind of work do you want more of? (Optional)
                          </label>
                          <textarea
                            id="workType"
                            name="workType"
                            value={formData.workType}
                            onChange={handleChange}
                            maxLength={500}
                            rows={3}
                            className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FF6B35]/30 focus:border-[#FF6B35] resize-none"
                            placeholder="e.g., Kitchen renovations, commercial plumbing..."
                          />
                          <div className="text-xs text-gray-400 mt-1 text-right">
                            {formData.workType.length}/500
                          </div>
                        </div>

                        <button
                          type="submit"
                          disabled={isSubmitting}
                          className="w-full py-4 bg-[#FF6B35] text-white rounded-xl font-semibold hover:bg-[#E85A2A] hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-50 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                        >
                          {isSubmitting ? 'Submitting...' : (
                            <>
                              Get Your Free Review
                              <i className="ri-arrow-right-line"></i>
                            </>
                          )}
                        </button>

                        {submitStatus === 'error' && (
                          <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm text-center">
                            Something went wrong. Please try again.
                          </div>
                        )}

                        <p className="text-sm text-center text-gray-500">
                          No credit card • No access required • 48-hour delivery
                        </p>
                      </form>
                    </>
                  )}
                </div>
              )}

              {/* Booking Tab */}
              {activeTab === 'booking' && (
                <div>
                  {loading ? (
                    <div className="flex items-center justify-center py-16">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF6B35]"></div>
                    </div>
                  ) : bookingStatus === 'success' ? (
                    <div className="text-center py-8">
                      <div className="w-16 h-16 bg-[#FF6B35]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <i className="ri-check-line text-3xl text-[#FF6B35]"></i>
                      </div>
                      <h3 className="text-2xl font-bold text-[#1A1F36] mb-2">Booking Confirmed!</h3>
                      <p className="text-gray-600">We'll call you at the scheduled time.</p>
                    </div>
                  ) : bookingStep === 0 ? (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                      {/* Calendar */}
                      <div>
                        <div className="flex items-center justify-between mb-4">
                          <button
                            type="button"
                            onClick={() => {
                              if (calendarMonth === 0) {
                                setCalendarMonth(11);
                                setCalendarYear(calendarYear - 1);
                              } else {
                                setCalendarMonth(calendarMonth - 1);
                              }
                            }}
                            className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-[#FF6B35]/10 transition-colors cursor-pointer"
                          >
                            <i className="ri-arrow-left-s-line text-xl"></i>
                          </button>
                          <h4 className="text-base font-semibold text-[#1A1F36]">
                            {monthNames[calendarMonth]} {calendarYear}
                          </h4>
                          <button
                            type="button"
                            onClick={() => {
                              if (calendarMonth === 11) {
                                setCalendarMonth(0);
                                setCalendarYear(calendarYear + 1);
                              } else {
                                setCalendarMonth(calendarMonth + 1);
                              }
                            }}
                            className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-[#FF6B35]/10 transition-colors cursor-pointer"
                          >
                            <i className="ri-arrow-right-s-line text-xl"></i>
                          </button>
                        </div>

                        <div className="grid grid-cols-7 gap-1 mb-2">
                          {dayNames.map(day => (
                            <div key={day} className="h-9 flex items-center justify-center text-xs font-medium text-gray-400">
                              {day}
                            </div>
                          ))}
                        </div>

                        <div className="grid grid-cols-7 gap-1">
                          {renderCalendar()}
                        </div>
                      </div>

                      {/* Time Slots */}
                      <div>
                        {selectedDate ? (
                          <>
                            <h4 className="text-sm font-semibold text-[#1A1F36] mb-4">
                              {formatDisplayDate(selectedDate)}
                            </h4>
                            {slotsForSelectedDate.length > 0 ? (
                              <div className="grid grid-cols-3 gap-2 max-h-[240px] overflow-y-auto pr-2">
                                {slotsForSelectedDate.map((slot, index) => (
                                  <button
                                    key={index}
                                    type="button"
                                    onClick={() => {
                                      setSelectedSlot(slot);
                                      setBookingStep(1);
                                    }}
                                    className="px-3 py-2.5 rounded-lg text-sm font-medium bg-[#FAFBFC] text-[#1A1F36] hover:bg-[#FF6B35] hover:text-white transition-all cursor-pointer border border-gray-100 hover:border-[#FF6B35]"
                                  >
                                    {formatTime(slot.start_time)}
                                  </button>
                                ))}
                              </div>
                            ) : (
                              <p className="text-gray-500 text-sm">No available times for this date.</p>
                            )}
                          </>
                        ) : (
                          <div className="flex items-center justify-center h-full text-gray-400 text-sm">
                            <p>Select a date to see available times</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div>
                      {/* Selected Time Summary */}
                      <div className="bg-[#FF6B35]/5 border border-[#FF6B35]/20 rounded-xl p-4 mb-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs text-[#FF6B35] uppercase tracking-wide font-semibold mb-1">Your Appointment</p>
                            <p className="text-base font-semibold text-[#1A1F36]">
                              {selectedDate && formatDisplayDate(selectedDate)}
                            </p>
                            <p className="text-sm text-[#1A1F36]">
                              {selectedSlot && `${formatTime(selectedSlot.start_time)} - ${formatTime(selectedSlot.end_time)}`}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => setBookingStep(0)}
                            className="text-sm text-[#FF6B35] font-medium underline hover:no-underline cursor-pointer"
                          >
                            Change
                          </button>
                        </div>
                      </div>

                      {/* Booking Form */}
                      <form onSubmit={handleBookingSubmit} className="space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label htmlFor="customer_name" className="block text-sm font-medium text-[#1A1F36] mb-2">
                              Your Name *
                            </label>
                            <input
                              type="text"
                              id="customer_name"
                              name="customer_name"
                              value={bookingForm.customer_name}
                              onChange={(e) => setBookingForm(prev => ({ ...prev, customer_name: e.target.value }))}
                              required
                              className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FF6B35]/30 focus:border-[#FF6B35]"
                              placeholder="John Smith"
                            />
                          </div>
                          <div>
                            <label htmlFor="customer_phone" className="block text-sm font-medium text-[#1A1F36] mb-2">
                              Phone Number *
                            </label>
                            <input
                              type="tel"
                              id="customer_phone"
                              name="customer_phone"
                              value={bookingForm.customer_phone}
                              onChange={(e) => setBookingForm(prev => ({ ...prev, customer_phone: e.target.value }))}
                              required
                              className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FF6B35]/30 focus:border-[#FF6B35]"
                              placeholder="07123 456789"
                            />
                          </div>
                        </div>

                        <div>
                          <label htmlFor="notes" className="block text-sm font-medium text-[#1A1F36] mb-2">
                            What would you like to discuss? (Optional)
                          </label>
                          <textarea
                            id="notes"
                            name="notes"
                            value={bookingForm.notes}
                            onChange={(e) => setBookingForm(prev => ({ ...prev, notes: e.target.value }))}
                            maxLength={500}
                            rows={3}
                            className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FF6B35]/30 focus:border-[#FF6B35] resize-none"
                            placeholder="e.g., Website improvements, Google Business Profile..."
                          />
                        </div>

                        <div className="flex gap-3 pt-2">
                          <button
                            type="button"
                            onClick={() => setBookingStep(0)}
                            className="flex-1 py-3.5 border-2 border-gray-200 text-[#1A1F36] rounded-xl font-semibold hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap"
                          >
                            Back
                          </button>
                          <button
                            type="submit"
                            disabled={bookingStatus === 'submitting'}
                            className="flex-1 py-3.5 bg-[#FF6B35] text-white rounded-xl font-semibold hover:bg-[#E85A2A] hover:shadow-lg transition-all disabled:opacity-50 cursor-pointer whitespace-nowrap"
                          >
                            {bookingStatus === 'submitting' ? 'Booking...' : 'Confirm Booking'}
                          </button>
                        </div>

                        {bookingStatus === 'error' && (
                          <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm text-center">
                            Something went wrong. Please try again.
                          </div>
                        )}
                      </form>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="text-center mt-10">
            <p className="text-base text-gray-600">
              Want ongoing support?{' '}
              <span className="font-semibold text-[#FF6B35]">
                Monthly insights from £150/month
              </span>
              —cancel anytime.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
